import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";
import { ADD_TO_CART_TEXT_ANIMATION_DURATION } from "@theme/product-form";

// TODO: FIX ME ONCE HEADER FINISHED
const HEADER_HEIGHT = 60;

// Distance the stop anchor's top must rise past the viewport bottom before the
// fallback park kicks in. Mirrors the two --stop-gap values in sticky-atc.liquid
// (30px mobile, 220px desktop) so the JS fallback parks at the same point the
// CSS view-timeline would, at the same breakpoint.
const PARK_GAP_MOBILE = 30;
const PARK_GAP_DESKTOP = 220;
const PARK_MOBILE_QUERY = "(max-width: 47.9375rem)";

class StickyAtcComponent extends Component {
  #abortController;
  /** @type {number | undefined} */
  #feedbackTimeout;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();

    const { signal } = this.#abortController;
    const section = this.closest(".shopify-section");
    const eventTarget = section || document;
    eventTarget.addEventListener(
      ThemeEvents.variantUpdate,
      this.#onVariantUpdate,
      { signal },
    );
    eventTarget.addEventListener(
      ThemeEvents.variantSelected,
      this.#onVariantSelected,
      { signal },
    );

    this.#setupScroll();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    clearTimeout(this.#feedbackTimeout);
  }

  #setupScroll() {
    const sectionId = this.dataset.sectionId;
    const atcButton = document.querySelector(
      `#shopify-section-${sectionId} .buy-buttons-block button[name="add"]`,
    );
    if (!atcButton) return;

    // Fallback for browsers without scroll-driven animations (Safari < 26,
    // Firefox): the CSS `@supports (animation-timeline: view())` park/fade in
    // sticky-atc.liquid never runs there, so the bar would stay pinned over the
    // footer legal text. Track the same stop anchor on the scroll pass and set
    // `data-parked` once its top rises into view near the bar — and keep it set
    // as you continue down to the footer (the anchor stays above the trigger).
    // Modern browsers use the CSS view-timeline instead, so skip this to avoid
    // two mechanisms driving the same properties.
    const useJsFallback = !CSS.supports("animation-timeline: view()");
    // The last anchor is the banner nearest the footer; if the section is
    // re-rendered (Section Rendering / theme editor) the cached node detaches
    // and reports top:0, so re-resolve whenever it's gone or disconnected.
    const resolveAnchor = () => {
      const anchors = document.querySelectorAll("[data-sticky-atc-stop]");
      return anchors[anchors.length - 1] ?? null;
    };
    const mobileQuery = window.matchMedia(PARK_MOBILE_QUERY);
    let parkAnchor = useJsFallback ? resolveAnchor() : null;

    const update = () => {
      // Reads first, then writes, to avoid layout thrash on the scroll pass.
      if (useJsFallback && (!parkAnchor || !parkAnchor.isConnected)) {
        parkAnchor = resolveAnchor();
      }
      const atcBottom = atcButton.getBoundingClientRect().bottom;
      const anchorTop = parkAnchor
        ? parkAnchor.getBoundingClientRect().top
        : null;

      const shown = atcBottom < HEADER_HEIGHT;
      const parkGap = mobileQuery.matches ? PARK_GAP_MOBILE : PARK_GAP_DESKTOP;
      const parked =
        anchorTop !== null && anchorTop <= window.innerHeight - parkGap;

      this.toggleAttribute("data-visible", shown);
      if (useJsFallback) this.toggleAttribute("data-parked", parked);
      // The bar is interactive only while shown and not parked; otherwise take
      // it out of the a11y tree and tab order so keyboard/SR users can't reach
      // a control that's visually hidden (`inert` also blocks the desktop
      // card's inner pointer-events-auto during the park transition).
      const interactive = shown && !parked;
      this.setAttribute("aria-hidden", String(!interactive));
      this.toggleAttribute("inert", !interactive);
    };
    let rafId = 0;
    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        update();
      });
    };
    update();
    const { signal } = this.#abortController;
    window.addEventListener("scroll", onScroll, { passive: true, signal });
    window.addEventListener("resize", onScroll, { passive: true, signal });
  }

  handleAtcClick() {
    const sectionId = this.dataset.sectionId;
    const atcButton = document.querySelector(
      `#shopify-section-${sectionId} .buy-buttons-block button[name="add"]`,
    );
    if (atcButton && !atcButton.disabled) {
      atcButton.click();
      this.#showAddedFeedback();
    }
  }

  #showAddedFeedback() {
    clearTimeout(this.#feedbackTimeout);
    const texts = this.querySelectorAll("[data-atc-text]");
    texts.forEach((el) => (el.textContent = "ADDED"));
    this.#feedbackTimeout = setTimeout(() => {
      texts.forEach((el) => (el.textContent = "ADD TO CART"));
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }

  /** @param {Event} event */
  #onVariantUpdate = (event) => {
    const { html } = /** @type {any} */ (event).detail.data;
    const resource = /** @type {any} */ (event).detail.resource;

    clearTimeout(this.#feedbackTimeout);
    const isSoldOut = !resource?.available;
    for (const btn of this.#atcButtons) {
      /** @type {HTMLButtonElement} */ (btn).disabled = isSoldOut;
      const textEl = btn.querySelector("[data-atc-text]");
      if (textEl) {
        textEl.textContent = isSoldOut ? "SOLD OUT" : "ADD TO CART";
      }
    }

    if (!html) return;

    const newPrice = html.querySelector(".add-to-cart-price");
    if (newPrice) {
      const priceText = newPrice.textContent.trim();
      if (this.refs.desktopPrice)
        this.refs.desktopPrice.textContent = priceText;
      if (this.refs.mobilePrice) this.refs.mobilePrice.textContent = priceText;
    }

    if (resource?.title && this.refs.variantTitle) {
      const productType = this.refs.variantTitle.dataset.productType;
      const variantTitle = resource.title.toUpperCase();
      this.refs.variantTitle.textContent = productType
        ? `${productType} / ${variantTitle}`
        : variantTitle;
    }
  };

  #onVariantSelected = () => {
    for (const btn of this.#atcButtons) {
      /** @type {HTMLButtonElement} */ (btn).disabled = true;
    }
  };

  get #atcButtons() {
    return [this.refs.desktopAtcButton, this.refs.mobileAtcButton].filter(
      Boolean,
    );
  }
}

if (!customElements.get("sticky-atc-component")) {
  customElements.define("sticky-atc-component", StickyAtcComponent);
}
